import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { ApiError } from '../shared/api/HttpClient';
import { AuthApi } from './AuthApi';
import { LoginScreen } from './LoginScreen';
import type { AuthUser } from './types';

export interface AuthenticatedContext {
  api: AuthApi;
  user: AuthUser;
  sessionKey: number;
  endSession: (notice?: string) => void;
}

interface AuthGateProps {
  path: string;
  children: (context: AuthenticatedContext) => ReactElement;
}

export function AuthGate({ path, children }: AuthGateProps): ReactElement {
  const api = useMemo(() => new AuthApi(), []);
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [sessionKey, setSessionKey] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void api.me().then((session) => {
      if (!active) return;
      setUser(session.user);
      setChecking(false);
    }).catch((reason: unknown) => {
      if (!active) return;
      if (!(reason instanceof ApiError) || reason.problem.status !== 401) setNotice('Не вдалося перевірити сесію. Увійдіть ще раз.');
      setUser(null);
      setChecking(false);
    });
    return () => { active = false; };
  }, [api]);

  useEffect(() => {
    const expired = (): void => {
      setUser(null);
      setNotice('Сесію завершено. Увійдіть знову, щоб продовжити.');
    };
    window.addEventListener('radio:session-expired', expired);
    return () => window.removeEventListener('radio:session-expired', expired);
  }, []);

  function authenticated(nextUser: AuthUser): void {
    setUser(nextUser);
    setSessionKey((value) => value + 1);
    setNotice(null);
    if (path === '/login' || path.startsWith('/login/')) window.location.replace('/admin');
  }

  function endSession(nextNotice = 'Ви вийшли з платформи.'): void {
    setUser(null);
    setNotice(nextNotice);
  }

  if (checking) return <main className="auth-loading" aria-live="polite"><span className="auth-loading-mark">SL</span><i /><p>Перевіряю захищену сесію…</p></main>;
  if (user === null) return <LoginScreen api={api} path={path} notice={notice} onAuthenticated={authenticated} />;

  return children({ api, user, sessionKey, endSession });
}
